import { useEffect, useState } from 'react';
import { api, type DriverCustomerRow } from '../lib/api';
import { DataTable, ErrorBox, Pager, usePageSize, dash, type Column, type Row } from '../components/DataTable';
import Modal from '../components/Modal';
import { canScreen, useMe } from '../lib/me';
import { useSticky } from '../lib/sticky';

/**
 * 회계 › 기사 거래처 — 기사 세금계산서 신청에서 고르는 거래처 목록(driver_customer)을 회계팀이 넣고 고친다(2026-10-07 게시판 #132).
 * 기사는 목록에 없는 거래처를 직접 못 넣는다(#132 앞 요청) → 새 거래처는 회계팀이 사업자등록증·이메일을 받아 여기서 등록.
 * 처음 목록은 dytaxdriver 업체리스트 시트에서 옮긴 것.
 */

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function DriverCustomersTab() {
  const { me } = useMe();
  const canWrite = canScreen(me, 'accounting', 'write');
  const [term, setTerm] = useSticky('acct.dcust.term', '');
  const [q, setQ] = useSticky('acct.dcust.q', '');
  const [PAGE, setPAGE] = usePageSize('acct.dcust', 200);
  const [page, setPage] = useState(0);
  const [nonce, setNonce] = useState(0);
  const [data, setData] = useState<{ rows: DriverCustomerRow[]; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<DriverCustomerRow | 'new' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => { setPage(0); }, [q, PAGE]);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    api.listDriverCustomers({ q: q || undefined, limit: PAGE, offset: page * PAGE })
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(errMsg(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [q, page, PAGE, nonce]);

  const columns: Column[] = [
    { key: 'cust_name', label: '거래처 이름', phone: 'pin' },
    { key: 'biz_number', label: '사업자번호', render: (r) => <span className="font-mono">{dash(r.biz_number)}</span> },
    { key: 'email', label: '발행 이메일', render: (r) => dash(r.email) },
    { key: 'invoice_n', label: '나간 계산서', align: 'right', phone: 'hide', render: (r) => (Number(r.invoice_n) ? `${r.invoice_n}건` : '') },
    { key: 'who', label: '등록·수정', phone: 'hide', noSort: true, render: (r) => {
      const by = String(r.updated_by ?? r.created_by ?? '');
      const at = String(r.updated_at ?? r.created_at ?? '').slice(0, 10);
      return <span className="text-xs text-ink-faint">{by.startsWith('sheet:') ? '업체리스트에서 옮김' : `${by.split('@')[0]} · ${at}`}</span>;
    } },
  ];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <form onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); }} className="flex items-center gap-2">
          <input className="field w-64" placeholder="거래처 이름·사업자번호·이메일" value={term} onChange={(e) => setTerm(e.target.value)} />
          <button className="btn-search" type="submit" aria-label="검색" title="검색" />
        </form>
        {data && <span className="text-sm text-ink-soft">전체 {data.total.toLocaleString('ko-KR')}곳</span>}
        {canWrite && <button className="btn-primary ml-auto" onClick={() => setEditing('new')}>거래처 등록</button>}
      </div>
      <p className="text-xs text-ink-faint">
        기사님이 세금계산서를 신청할 때 고르는 거래처 목록입니다. 목록에 없는 거래처는 기사님이 직접 넣을 수 없으니,
        사업자등록증과 발행 이메일을 받아 여기서 등록해 주세요. 등록하면 바로 기사님 화면 목록에 나옵니다. 줄을 누르면 고칩니다.
      </p>
      {notice && <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{notice}</p>}
      {error && <ErrorBox message={error} />}
      <DataTable tableId="accounting.dcust" columns={columns} rows={(data?.rows ?? []) as unknown as Row[]} loading={loading}
                 onRowClick={canWrite ? (r) => setEditing(r as unknown as DriverCustomerRow) : undefined} />
      <Pager page={page} setPage={setPage} rowCount={data?.rows.length ?? 0} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={200} />
      {editing && (
        <Modal width="max-w-lg">
          <DriverCustomerForm row={editing === 'new' ? null : editing}
                              onClose={() => setEditing(null)}
                              onSaved={(msg) => { setEditing(null); setNotice(msg); setNonce((n) => n + 1); }} />
        </Modal>
      )}
    </div>
  );
}

function DriverCustomerForm({ row, onClose, onSaved }: {
  row: DriverCustomerRow | null; onClose: () => void; onSaved: (notice: string | null) => void;
}) {
  const [f, setF] = useState({ custName: row?.cust_name ?? '', bizNumber: row?.biz_number ?? '', email: row?.email ?? '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF((x) => ({ ...x, [k]: e.target.value }));

  async function save() {
    setBusy(true); setError(null);
    try {
      const r = await api.saveDriverCustomer({ id: row ? Number(row.driver_customer_id) : undefined, ...f });
      const notes = [r.sheetError, r.sameBiz.length ? `같은 사업자번호로 등록된 거래처가 더 있습니다: ${r.sameBiz.join(', ')}` : null].filter(Boolean);
      onSaved(notes.length ? `저장했습니다. ${notes.join(' ')}` : null);
    } catch (e) {
      setError(errMsg(e));
    } finally { setBusy(false); }
  }

  async function remove() {
    if (!row) return;
    if (!window.confirm(`「${row.cust_name}」을(를) 기사 거래처 목록에서 지울까요? 이미 발행된 계산서는 그대로 남습니다.`)) return;
    setBusy(true); setError(null);
    try {
      const r = await api.deleteDriverCustomer({ id: Number(row.driver_customer_id) });
      onSaved(r.sheetError ? `지웠습니다. ${r.sheetError}` : null);
    } catch (e) {
      setError(errMsg(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="card space-y-4 p-5">
      <h2 className="font-semibold">{row ? '기사 거래처 수정' : '기사 거래처 등록'}</h2>
      <div className="grid gap-3 text-sm">
        <label className="space-y-1">거래처 이름(사업자등록증 상호)
          <input className="field w-full" value={f.custName} onChange={set('custName')} autoFocus />
        </label>
        <label className="space-y-1">사업자번호
          <input className="field w-full font-mono" value={f.bizNumber} onChange={set('bizNumber')} placeholder="000-00-00000" />
          <span className="block text-xs text-muted">번호가 없는 곳(나라장터·서류 건)은 비우거나 「나라장터」처럼 글자로 적어도 됩니다.</span>
        </label>
        <label className="space-y-1">발행 이메일
          <input className="field w-full" value={f.email} onChange={set('email')} placeholder="계산서를 받을 메일" type="email" />
        </label>
      </div>
      {error && <ErrorBox message={error} />}
      <div className="flex gap-2">
        <button className="btn-primary" onClick={save} disabled={busy}>{busy ? '저장 중…' : '저장'}</button>
        <button className="btn-ghost" onClick={onClose}>닫기</button>
        {row && <button className="btn-ghost ml-auto text-red-600" onClick={remove} disabled={busy}>지우기</button>}
      </div>
    </div>
  );
}
